'use client';

import './infinite-scroll.css';

import { getPuzzle } from '@wt/app/game/_utils/getPuzzle';
import { GameTile } from '@wt/shared/components/games/GameTile';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { getCommunityGames, getSocialsForUser } from '@wt/utilities/database';
import { isEmpty } from 'lodash';
import moment from 'moment';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { CommunityGameWithContent } from '../types';
import { GAMES_PER_LOAD } from './CommunityContent';
import {
  getSocialPlatformInfo,
  isSocialHandleValid,
  sanitiseSocialLinks,
} from '@wt/utilities/socials';

export const CommunityGamesTabContent = ({
  sortBy,
  daysPeriod,
  sortOrder,
  handleClickGame,
}: {
  sortBy: string;
  daysPeriod: number;
  sortOrder: string;
  handleClickGame: (slug: string) => void;
}) => {
  const searchParams = useSearchParams();
  const usernameFilter = searchParams.get('username');
  const [gamesCache, setGamesCache] = useState<
    Map<string, CommunityGameWithContent[]>
  >(new Map());
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const gamesContainer = useRef<HTMLDivElement>(null);
  const [userSocials, setUserSocials] = useState<{
    photoURL: string;
    bio: string;
    socials: { platform: string; handle: string }[];
  } | null>(null);

  useEffect(() => {
    if (usernameFilter) {
      getSocialsForUser(usernameFilter).then(setUserSocials);
    } else {
      setUserSocials(null);
    }
  }, [usernameFilter]);

  const gamesKey = useMemo(() => {
    return (
      sortBy +
      ':' +
      (usernameFilter ?? '') +
      ':' +
      (daysPeriod ?? '') +
      ':' +
      sortOrder
    );
  }, [sortBy, daysPeriod, sortOrder, usernameFilter]);

  const games = useMemo(() => {
    if (gamesCache.has(gamesKey)) {
      return gamesCache.get(gamesKey) ?? [];
    }
    return [];
  }, [gamesCache, gamesKey]);

  const loadNext = useCallback(async () => {
    const { games: loadedGames, totalCount: loadedTotalCount } =
      await getCommunityGames({
        sortBy,
        daysPeriod,
        sortOrder,
        username: usernameFilter,
        offset: games.length,
        limit: GAMES_PER_LOAD,
      });
    const promises: Promise<CommunityGameWithContent>[] = [];
    loadedGames.forEach((g) => {
      promises.push(
        getPuzzle(g.slug, 'community').then((rounds) => {
          const gameWithContent: CommunityGameWithContent = {
            ...g,
            rounds,
          };
          return gameWithContent;
        })
      );
    });
    const g = await Promise.all(promises);
    setGamesCache(new Map([...gamesCache, [gamesKey, [...games, ...g]]]));
    setTotalCount(loadedTotalCount);
  }, [
    sortBy,
    sortOrder,
    daysPeriod,
    usernameFilter,
    games,
    setTotalCount,
    setGamesCache,
  ]);

  useEffect(() => {
    (async () => {
      // If sortBy / usernameFilter changes, then we perform an initial load
      await loadNext();
      setLoading(false);
    })();
  }, [sortBy, sortOrder, daysPeriod, usernameFilter]);

  const hasMore = totalCount > games.length;

  return (
    <div className="mb-5 mt-3 flex w-full flex-col items-center justify-center px-4 md:my-5">
      <div className="flex w-full flex-col items-center gap-3 md:max-w-screen-xl">
        {usernameFilter && userSocials && (
          <UserSocialsBox username={usernameFilter} userSocials={userSocials} />
        )}

        <div ref={gamesContainer}>
          {loading && <Spinner />}
          {!loading && (
            <InfiniteScroll
              dataLength={games.length}
              next={loadNext}
              hasMore={hasMore}
              loader={
                <div className="mt-4 flex w-full justify-center">
                  <Spinner />
                </div>
              }
              endMessage={<>{isEmpty(games) && <EmptyGamesContent />}</>}
            >
              <GamesContent
                sortBy={sortBy}
                daysPeriod={daysPeriod}
                sortOrder={sortOrder}
                games={games}
                handleClickGame={handleClickGame}
              />
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

const UserSocialsBox = ({
  username,
  userSocials,
}: {
  username: string;
  userSocials: {
    bio: string;
    photoURL: string;
    socials: { platform: string; handle: string }[];
  };
}) => {
  return (
    <div className="relative mb-2 flex flex-col md:flex-row w-full items-center justify-center">
      <div className="md:w-52 md:mr-4 flex flex-col items-center">
        <div
          className="w-24 h-24 rounded-full mb-2.5 bg-cover bg-center"
          style={{ backgroundImage: `url(${userSocials.photoURL})` }}
        ></div>
        <div className="bg-pale-highlight rounded-lg text-info text-center text-base py-1 truncate px-1 max-w-[100%]">
          @{username}
        </div>
      </div>
      <div className="flex-1 pt-2 md:pt-6">
        <div className="flex gap-3 justify-center md:justify-start">
          {userSocials.socials && <SocialLinks socials={userSocials.socials} />}
        </div>
        {userSocials.bio && (
          <div className="rounded-lg mt-3 py-3 px-4 bg-base-200 text-left max-w-[560px]">
            {userSocials.bio}
          </div>
        )}
      </div>
    </div>
  );
};

const SocialLink = ({
  platform,
  handle,
}: {
  platform: string;
  handle: string;
}) => {
  const { name, Icon } = getSocialPlatformInfo(platform);
  if (!name) return null;

  const isValid = isSocialHandleValid(platform, handle);
  if (!isValid) return null;

  const href = sanitiseSocialLinks(platform, handle);

  return (
    <a target="_blank" href={href} className={'w-8 h-8 inline-block'}>
      {Icon}
    </a>
  );
};

const SocialLinks = ({
  socials,
}: {
  socials: { platform: string; handle: string }[];
}) => {
  return (
    socials?.length > 0 && (
      <>
        {socials.map((s) => (
          <SocialLink platform={s.platform} handle={s.handle} />
        ))}
      </>
    )
  );
};

const GamesContent = ({
  sortBy,
  daysPeriod,
  sortOrder,
  games,
  handleClickGame,
}: {
  sortBy: string;
  daysPeriod: number;
  sortOrder: string;
  games: CommunityGameWithContent[];
  handleClickGame: (slug: string) => void;
}) => {
  return (
    <>
      {!isEmpty(games) && (
        <GamesList
          sortBy={sortBy}
          daysPeriod={daysPeriod}
          sortOrder={sortOrder}
          games={games}
          handleClickGame={handleClickGame}
        />
      )}
    </>
  );
};

const GamesList = ({
  sortBy,
  daysPeriod,
  sortOrder,
  games,
  handleClickGame,
}: {
  sortBy: string;
  daysPeriod: number;
  sortOrder: string;
  games: CommunityGameWithContent[];
  handleClickGame: (slug: string) => void;
}) => {
  return (
    <ul className="grid w-full grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-4">
      {games.map((g, i) => (
        <div key={i}>
          <GameTile
            game={g}
            handleClick={handleClickGame}
            features={{
              name: true,
              playedTick: true,
              likes: sortBy == 'likesScore',
              timesPlayed: sortBy == 'timesPlayedScore',
              daysPeriod,
              clickToPlay: true,
            }}
          />
          <div className="flex px-1 py-2 text-sm">
            <div className="flex-1">{moment(g.creationDate).fromNow()}</div>
            <Link
              href={`/community?username=${g.username}`}
              className="italic text-highlight dark:text-highlight-dark"
            >
              by @{g.username}
            </Link>
          </div>
        </div>
      ))}
    </ul>
  );
};

const EmptyGamesContent = () => {
  return (
    <div className="mt-10 flex w-full flex-col items-center justify-center gap-4">
      <span className="text-center text-sm md:text-lg">
        No public games found.
      </span>
    </div>
  );
};
